<template>
  <div
    ref="reportElementWrapper"
    :class="reportElementClass"
    :draggable="editing"
    :dropzone="editing"
    @dragstart="dragStart($event)"
    @dragend="dragEnd"
    @drop="drop"
    @dragover.prevent
    @dragenter="dragEnter"
    @dragleave="dragLeave"
    @click.shift.stop="select()"
    @click.exact.stop="unselect()"
  >
    <template v-if="editing && reportElement.migratable">
      <div class="q-pa-md q-gutter-sm">
        <q-banner
          v-if="$isFeatureEnabled($featureFlags.site_report_migration)"
          dense
          inline-actions
          class="bg-red-1 migratable-banner"
        >
          Outdated Element
          <template #action>
            <q-btn
              flat
              label="Duplicate"
              @click="$emit('duplicate-elements', [reportElement.id])"
            />
            <q-btn
              flat
              label="Migrate"
              @click="migrateElement(reportElement)"
            />
            <q-btn
              flat
              label="Delete"
              @click="$emit('delete-elements', [reportElement.id])"
            />
            <q-btn
              unelevated
              round
              icon="question_mark"
              color="red"
              size="sm"
              @click="migrateElementHelpDialog = true"
            />
            <q-dialog v-model="migrateElementHelpDialog">
              <q-card>
                <q-card-section class="row items-center q-pb-none">
                  <div class="text-h6">
                    Outdated Element
                  </div>
                  <q-space />
                  <q-btn
                    v-close-popup
                    icon="close"
                    flat
                    round
                    dense
                  />
                </q-card-section>

                <q-card-section>
                  This {{ reportElement.title }} report element is outdated and should be replaced with the updated
                  element for full functionality.
                  <img
                    class="full-width q-mx-md"
                    alt="Migrate Report Element Actions"
                    src="/images/admin/Migrate Report Element Actions.jpg"
                  >

                  To update, you can click <b> Duplicate </b> to first duplicate your report element to have a backup
                  copy.<br>
                  Click <b>Migrate</b> to update your report element to the latest version.<br>
                  You may then <b> Delete </b> the outdated report element.
                </q-card-section>
              </q-card>
            </q-dialog>
          </template>
        </q-banner>
      </div>
    </template>
    <q-card
      v-if="reportElement.reportStatus.error || reportElement.reportStatus.noData && !reportElement.reportStatus.done && !editing"
      square
      flat
      bordered
    >
      <q-card-section>
        <div class="text-bold">
          {{ reportElement.title }}
        </div>
        <div
          v-if="reportElement.reportStatus.error"
          class="q-my-lg q-mx-sm text-negative"
        >
          <q-icon
            name="error"
            size="sm"
          />
          Error: {{ reportElement.reportStatus.errorMessage }}
        </div>

        <div
          v-else
          class="q-my-xl"
        >
          <report-migratable-button
            v-if="!printing && !editing && reportElement.migratable"
            @edit="$emit('edit', true)"
          />
          <report-status :report-status="reportElement.reportStatus" />
        </div>
      </q-card-section>
    </q-card>
    <template v-else>
      <report-chart
        v-if="report.loaded && reportElement.type === 'chart'"
        :loading="report.loading"
        :chart="reportElement.chart"
        :report="report"
        :report-element="reportElement"
        :report-status="reportElement.reportStatus"
        :report-options="reportElement.finalOptions"
        :title="reportElement.title"
        :printing="printing"
        :editing="editing"
        @edit="$emit('edit', true)"
        @show-source="cell => showSource(cell)"
      />
      <report-table
        v-else-if="reportElement.type === 'table'"
        ref="reportTable"
        square
        :loading="report.loading"
        :table="reportElement.table"
        :report="report"
        :report-element="reportElement"
        :report-status="reportElement.reportStatus"
        :report-options="reportElement.reportOptions"
        :title="reportElement.title"
        :printing="printing"
        :editing="editing"
        :repeat-headers="reportElement.classes.includes('report-col-12')"
        :time-zone="timeZone"
        :search-query="searchQuery"
        :simple-view="simpleView"
        @export-data="includeGrandTotals => exportData(reportElement, includeGrandTotals)"
        @show-source="cell => showSource(cell)"
        @show-data-source="cell => showDataTable(cell)"
        @show-trend="cell => showTrend(cell, false)"
        @show-drill-down="cell => showTrend(cell, true)"
        @show-comparison="cell => showComparison(cell)"
        @edit-groupings="showGroupingSelector = true"
        @edit-filters="showReportFilters = true"
        @edit-columns="showColumnSelector = true"
        @edit="$emit('edit', true)"
        @edit-element="$emit('edit-element', $event)"
      />
      <report-view
        v-else-if="reportElement.type === 'view' && reportElement.view.multipleElements === false"
        :loading="report.loading"
        :view="reportElement.view"
        :title="reportElement.title"
        :printing="printing"
        :editing="editing"
        :repeat-headers="reportElement.classes.includes('report-col-12')"
        :time-zone="timeZone"
        @export-data="exportData(reportElement)"
      />
      <report-view-multiples
        v-else-if="reportElement.type === 'view' && reportElement.view.multipleElements === true"
        :loading="report.loading"
        :view="reportElement.view"
        :title="reportElement.title"
        :printing="printing"
        :editing="editing"
        :repeat-headers="reportElement.classes.includes('report-col-12')"
        :time-zone="timeZone"
        @export-data="exportData(reportElement)"
      />
      <widget
        v-else-if="reportElement.type === 'widget'"
        :report-element="reportElement"
        :editing="editing"
        @edit="$emit('edit', true)"
      />
      <schedule-view
        v-else-if="reportElement.type === 'schedule_view' && currentSite"
        style="position: relative;"
        :date="reportElement.finalOptions.date"
        :save-label="'Save ' + report.scopeType + ' Report'"
        :can-save="editing"
        :site-id="currentSite.id"
        :screen-ids="reportElement.finalOptions.screen_ids"
        :show-time-header="reportElement.finalOptions.show_time_header"
        :show-time-slot-header="reportElement.finalOptions.show_time_slot_header"
        :show-screen-labels="reportElement.finalOptions.show_screen_labels"
        :show-times="reportElement.finalOptions.show_times"
        :show-time-chips="reportElement.finalOptions.show_time_chips"
        :show-seat-chart="reportElement.finalOptions.show_seat_chart"
        :show-rating="reportElement.finalOptions.show_rating"
        :show-price-card="reportElement.finalOptions.show_price_card"
        :show-showing-badges="reportElement.finalOptions.show_showing_badges"
        :show-posters="reportElement.finalOptions.show_posters"
        :show-progress-bar="reportElement.finalOptions.show_progress_bar"
        :color-by-occupancy="reportElement.finalOptions.color_by_occupancy"
        :highlight-starting-showings="reportElement.finalOptions.highlight_starting_showings"
        :highlight-ending-showings="reportElement.finalOptions.highlight_ending_showings"
        :highlight-current-showings="reportElement.finalOptions.highlight_current_showings"
        :show-relative-times="reportElement.finalOptions.show_relative_times"
        :show-relative-time-chips="reportElement.finalOptions.show_relative_time_chips"
        :show-current-time="reportElement.finalOptions.show_current_time"
        :hide-distance-showings="reportElement.finalOptions.hide_distance_showings"
        :distant-showing-threshold-hours="+reportElement.finalOptions.distant_showing_threshold_hours"
        :zoom="+reportElement.finalOptions.zoom || 1"
        @save="config => {
          reportElement.saveOptionChange({
            screen_ids: config.screenIds,
            show_time_header: config.showTimeHeader,
            show_time_slot_header: config.showTimeSlotHeader,
            show_screen_labels: config.showScreenLabels,
            show_times: config.showTimes,
            show_time_chips: config.showTimeChips,
            show_rating: config.showRating,
            show_seat_chart: config.showSeatChart,
            show_price_card: config.showPriceCard,
            show_showing_badges: config.showShowingBadges,
            show_posters: config.showPosters,
            show_progress_bar: config.showProgressBar,
            highlight_starting_showings: config.highlightStartingShowings,
            highlight_ending_showings: config.highlightEndingShowings,
            highlight_current_showings: config.highlightCurrentShowings,
            color_by_occupancy: config.colorByOccupancy,
            show_relative_times: config.showRelativeTimes,
            show_relative_time_chips: config.showRelativeTimeChips,
            show_current_time: config.showCurrentTime,
            hide_distance_showings: config.hideDistanceShowings,
            distant_showing_threshold_hours: +config.distantShowingThresholdHours,
            zoom: +config.zoom,
          })
        }"
      />
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-else-if="reportElement.type === 'html'"
        v-html="reportElement.html.html"
      />
      <profit-and-loss-report-table
        v-else-if="reportElement.type === 'profit_and_loss_table'"
        :loading="report.loading"
        :table="reportElement.table"
        :report="report"
        :report-element="reportElement"
        :report-status="reportElement.reportStatus"
        :report-options="reportElement.finalOptions"
        :title="reportElement.title"
        :printing="printing"
        :editing="editing"
        @export-data="exportData(reportElement)"
        @show-source="cell => showSource(cell)"
        @show-trend="cell => showTrend(cell, false)"
        @show-drill-down="cell => showTrend(cell, true)"
        @show-comparison="cell => showComparison(cell)"
        @edit-groupings="showGroupingSelector = true"
        @edit-filters="showReportFilters = true"
        @edit-columns="showColumnSelector = true"
      />
    </template>
    <report-element-menu
      :editing="editing"
      :from-atlas="fromAtlas"
      :report-element="reportElement"
      :selected-element-ids="selectedElementIds"
      :copied-element-ids="copiedElementIds"
      :copied-element="copiedElement"
      @edit="$emit('edit-element', reportElement)"
      @advanced-edit="$emit('advanced-edit-element', reportElement)"
      @copy-elements="ids => $emit('copy-elements', ids)"
      @delete-elements="ids => $emit('delete-elements', ids)"
      @duplicate-elements="ids => $emit('duplicate-elements', ids)"
      @migrate-element="migrateElement(reportElement)"
      @paste-elements="element => $emit('paste-elements', reportElement)"
      @paste-special="type => $emit('paste-special', type)"
      @show-source="showSource()"
      @edit-columns="showColumnSelector = true"
      @edit-groupings="showGroupingSelector = true"
      @edit-filters="showReportFilters = true"
    />
    <report-data-display-dialog
      v-model="showGroupingSelector"
      :list="reportElement.reportOptions.group_by"
      :options="reportElement.possibleOptionsValues.group_by || []"
      :columns="reportElement.columns || []"
      title="Groupings"
      :ordering="true"
      @list-updated="list => { reportElement.reportOptions.group_by = list; previewChanges() }"
    />
    <report-data-display-dialog
      v-model="showColumnSelector"
      :list="reportElement.reportOptions.column_order"
      :options="$_(columns).filter(column => !column.groupable).map('field').value() || []"
      :columns="reportElement.columns || []"
      title="Columns"
      :ordering="true"
      @list-updated="list => { reportElement.reportOptions.column_order = list; previewChanges() }"
    />
    <report-edit-filter-dialog
      v-model="showEditFilter"
      :field="editFilterField"
      :report="report"
      :report-element="reportElement"
      @preview-changes="previewChanges()"
    />
    <source-data-report-dialog
      v-model="showSourceDialog"
      :report-options="sourceOptions"
      :title="sourceTitle"
      :atlas="fromAtlas"
    />
    <data-table-report-dialog
      v-model="showDataTableDialog"
      :report-options="dataTableDialogOptions"
      :title="dataTableDialogTitle"
      :atlas="fromAtlas"
    />
    <comparison-report-dialog
      v-model="showComparisonDialog"
      :report-options="comparisonOptions"
      :title="comparisonTitle"
      :atlas="fromAtlas"
    />
    <trend-report-dialog
      v-model="showTrendDialog"
      :report-options="trendOptions"
      :title="trendTitle"
      :atlas="fromAtlas"
    />
    <report-filters-dialog
      v-model="showReportFilters"
      :report-element="reportElement"
      @edit-filter="field => editFilters(field)"
    />
  </div>
</template>

<script>

import { date, exportFile } from 'quasar'
const { formatDate, addToDate, getDateDiff } = date
import { IsoDateTime } from 'src/models/model'
import { isComponentSourceCents } from 'src/models/report/ReportElement'
import ComparisonReportDialog from 'src/modules/admin/report/ComparisonReportDialog'
import DataTableReportDialog from 'src/modules/admin/report/DataTableReportDialog'
import ProfitAndLossReportTable from 'src/modules/admin/report/ProfitAndLossReportTable'
import ReportDataDisplayDialog from 'src/modules/admin/report/ReportDataDisplayDialog'
import ReportEditFilterDialog from 'src/modules/admin/report/ReportEditFilterDialog'
import ReportElementMenu from 'src/modules/admin/report/ReportElementMenu'
import ReportFiltersDialog from 'src/modules/admin/report/ReportFiltersDialog'
import ReportMigratableButton from 'src/modules/admin/report/ReportMigratableButton'
import ReportStatus from 'src/modules/admin/report/ReportStatus'
import ReportView from 'src/modules/admin/report/ReportView'
import ReportViewMultiples from 'src/modules/admin/report/ReportViewMultiples'
import ScheduleView from 'src/modules/admin/report/ScheduleView'
import SourceDataReportDialog from 'src/modules/admin/report/SourceDataReportDialog'
import TrendReportDialog from 'src/modules/admin/report/TrendReportDialog'
import Widget from 'src/modules/admin/report/Widget'
import ReportChart from 'src/modules/admin/report-chart/ReportChart'
import ReportTable from 'src/modules/admin/report-table/ReportTable'

function wrapCsvValue (val) {
  let formatted = val

  formatted = formatted === null ? '' : String(formatted)

  formatted = formatted.split('"').join('""').split('\n').join('\\n').split('\r').join('\\r')

  return `"${formatted}"`
}

export default {
  name: 'ReportElement',
  components: {
    ReportTable,
    ReportView,
    Widget,
    ReportElementMenu,
    ReportViewMultiples,
    ScheduleView,
    ReportStatus,
    ReportMigratableButton,
    ReportChart,
    ReportDataDisplayDialog,
    ReportEditFilterDialog,
    ReportFiltersDialog,
    SourceDataReportDialog,
    ComparisonReportDialog,
    TrendReportDialog,
    ProfitAndLossReportTable,
    DataTableReportDialog
  },
  props: {
    reportElement: {
      type: Object,
      required: true
    },
    report: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: false,
      default: 0
    },
    printing: {
      type: Boolean,
      required: false,
      default: false
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    selectedElementIds: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    copiedElementIds: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    copiedElement: {
      type: Object,
      required: false,
      default: null
    },
    searchQuery: {
      type: String,
      default: ''
    },
    simpleView: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['edit-element', 'migrate-element', 'advanced-edit-element', 'copy-elements', 'delete-elements', 'duplicate-elements', 'paste-elements', 'paste-special', 'drag-start', 'drag-end', 'select-element', 'unselect-elements', 'edit'],
  data () {
    return {
      showColumnSelector: false,

      showGroupingSelector: false,
      showEditFilter: false,
      editFilterField: null,
      showReportFilters: false,

      showSourceDialog: false,
      sourceOptions: {},
      sourceTitle: null,

      showDataTableDialog: false,
      dataTableDialogOptions: {},
      dataTableDialogTitle: null,

      showComparisonDialog: false,
      comparisonOptions: {},
      comparisonTitle: null,

      showTrendDialog: false,
      trendOptions: {},
      trendTitle: null,

      dragOver: 0,
      dragging: false,
      dropAfter: false,
      dropClass: 'drop-left',

      migrateElementHelpDialog: false

    }
  },
  computed: {
    columns () {
      return this.reportElement?.table?.columns?.data || this.reportElement.chart?.columns?.data || []
    },
    selected () {
      return _.includes(this.selectedElementIds, this.reportElement?.id)
    },
    timeZone () {
      return this.report.timeZone || this.currentSite?.timeZone
    },
    fromAtlas () {
      return this.report.atlas
    },
    reportElementClass () {
      const classes = []

      if (_.isNil(this.reportElement?.type)) {
        return ''
      }

      if (this.reportElement.type === 'profit_and_loss_table') {
        return 'report-col-12'
      }

      classes.push(this.reportElement.classes)

      if (this.dragOver) {
        classes.push('drag-over')
      }

      if (this.dragging) {
        classes.push('dragging')
      }

      if (this.selected) {
        classes.push('selected')
      }

      if (this.reportElement.type !== 'view') {
        classes.push('report-element')
      }

      return classes.join(' ')
    }
  },
  configuration: ['currentSite', 'currentCircuit'],
  methods: {
    async editFilters (field) {
      this.editFilterField = field
      this.showEditFilter = true
    },
    async migrateElement (element) {
      await element.migrateElement()
      element.reportOptions = element.defaultOptions
      await this.report.runReport()
    },
    showSource (cell = null) {
      this.sourceOptions = {
        ...this.reportElement.finalOptions
      }

      const title = [this.report.name, this.reportElement.title]

      if (!cell) {
        // This is for the entire element no filtering
      } else if (cell.row) {
        // This came from a table
        let filterFields

        if (cell.row.grandTotal) {
          // Grand total has no filtering
          filterFields = null
        } else {
          // Only apply filters if the cell has a value. This supports subtotal rows to be inspectable.
          filterFields = _.filter(this.reportElement.finalOptions.group_by, field => cell.row[field]?.hasValue)
        }

        _.each(filterFields, field => {
          const column = _.find(this.columns, { field })

          if (!column || !column.groupable) return

          // Filter to just this row
          const value = cell.row[field]?.hasValue ? cell.row[field].value : cell.row[field]
          this.sourceOptions[`exclude_${field}`] = false
          this.sourceOptions[`${field}_filter`] = [value]
        })

        this.sourceOptions.highlight_field = cell.col.field

        title.push(cell.key)
        title.push(cell.col.label)
        if (cell.col.component === 'money') {
          title.push(`${this.currentCircuit.localeCurrencySymbol}${Math.round(cell.value.value * 100) / 100}`)
        } else if (isComponentSourceCents(cell.col.component)) {
          title.push(`${this.currentCircuit.localeCurrencySymbol}${cell.value.value / 100}`)
        } else {
          title.push(cell.value.value)
        }
      } else if (cell.value && this.reportElement.finalOptions.group_by.length === 1) {
        // This came from an aggregate chart
        const field = this.reportElement.reportOptions.group_by[0]
        const column = _.find(this.columns, { field })

        if (!column || !column.groupable) return

        // Filter to just this row
        const value = cell.groupValue
        this.sourceOptions[`exclude_${field}`] = false
        this.sourceOptions[`${field}_filter`] = [value]

        this.sourceOptions.highlight_field = cell.col.field

        title.push(cell.groupValue)
        title.push(cell.col.label)
        if (cell.col.component === 'money' || (isComponentSourceCents(cell.col.component))) {
          title.push(`${this.currentCircuit.localeCurrencySymbol}${cell.value}`)
        } else {
          title.push(cell.value.value)
        }
      }

      // Ensure we show all columns
      this.sourceTitle = title.join(' > ')

      // If the column order has been set or groupings have been added,
      // we want to reveal those columns in the source column order as well
      this.sourceOptions.column_order = _([this.reportElement.finalOptions.group_by,
        this.reportElement.finalOptions.column_order,
        this.sourceOptions.source_column_order]).map(_.values).flatten().uniq().compact().value()

      this.showSourceDialog = true
    },
    showTrend (cell = null, drillDown = false) {
      this.trendOptions = {
        ...this.reportElement.finalOptions
      }

      if (drillDown) {
        // For now just show the days of this inner range, later detect if we support hour
        this.trendOptions.interval = 'day'
      } else {
        const daySpan = getDateDiff(this.trendOptions.end_date, this.trendOptions.start_date, 'days') + 1

        this.trendOptions.highlight_start_date = this.trendOptions.start_date
        this.trendOptions.highlight_end_date = this.trendOptions.end_date

        if (daySpan < 7) {
          this.trendOptions.interval = 'day'
          this.trendOptions.start_date = formatDate(addToDate(this.trendOptions.start_date, { days: -daySpan }, 'YYYY-MM-DD'))
          this.trendOptions.end_date = formatDate(addToDate(this.trendOptions.end_date, { days: daySpan }, 'YYYY-MM-DD'))
        } else if (daySpan < 90) {
          this.trendOptions.interval = 'week'
          this.trendOptions.start_date = formatDate(addToDate(this.trendOptions.start_date, { days: -daySpan }, 'YYYY-MM-DD'))
          this.trendOptions.end_date = formatDate(addToDate(this.trendOptions.end_date, { days: daySpan }, 'YYYY-MM-DD'))
        } else {
          this.trendOptions.interval = 'week'
          this.trendOptions.start_date = formatDate(addToDate(this.trendOptions.start_date, { days: -daySpan }, 'YYYY-MM-DD'))
          this.trendOptions.end_date = formatDate(addToDate(this.trendOptions.end_date, { days: daySpan }, 'YYYY-MM-DD'))
        }
      }

      const title = [this.report.name, this.reportElement.title]

      if (cell.row) {
        // This came from a table

        let filterFields
        if (cell.row.grandTotal) {
          // Grand total has no filtering
          filterFields = null
        } else {
          // Only apply filters if the cell has a value. This supports subtotal rows to be inspectable.
          filterFields = _.filter(this.reportElement.finalOptions.group_by, field => cell.row[field]?.hasValue)
        }

        _.each(filterFields, field => {
          const column = _.find(this.columns, { field })

          if (!column || !column.groupable) return

          // Filter to just this row
          const value = cell.row[field]?.hasValue ? cell.row[field].value : cell.row[field]
          this.trendOptions[`exclude_${field}`] = false
          this.trendOptions[`${field}_filter`] = [value]
        })

        this.trendOptions.output_type = cell.col.field

        title.push(cell.key)
        title.push(cell.col.label)
        if (cell.col.component === 'money') {
          title.push(`${this.currentCircuit.localeCurrencySymbol}${cell.value.value}`)
        } else if (isComponentSourceCents(cell.col.component)) {
          title.push(`${this.currentCircuit.localeCurrencySymbol}${cell.value.value / 100}`)
        } else {
          title.push(cell.value.value)
        }
      } else if (cell.value && this.reportElement.finalOptions.group_by.length === 1) {
        // This came from an aggregate chart
        const field = this.reportElement.finalOptions.group_by[0]
        const column = _.find(this.columns, { field })

        if (!column || !column.groupable) return

        // Filter to just this row
        const value = cell.groupValue
        this.trendOptions[`exclude_${field}`] = false
        this.trendOptions[`${field}_filter`] = [value]

        this.trendOptions.output_type = cell.col.field

        title.push(cell.groupValue)
        title.push(cell.col.label)

        if (drillDown) {
          if (cell.col.component === 'money' || isComponentSourceCents(cell.col.component)) {
            title.push(`${this.currentCircuit.localeCurrencySymbol}${cell.value}`)
          } else {
            title.push(cell.value.value)
          }
        }
      }

      // Ensure we show all columns
      this.trendTitle = title.join(' > ')

      this.showTrendDialog = true
    },
    showComparison (cell) {
      this.comparisonOptions = {
        ...this.reportElement.finalOptions
      }

      const title = [this.report.name, this.reportElement.title, cell.col.label]

      this.comparisonOptions.output_type = cell.col.field

      this.comparisonTitle = title.join(' > ')

      this.showComparisonDialog = true
    },
    showDataTable (cell = null) {
      this.dataTableDialogOptions = {
        ...cell.row.data_source.options
      }

      const title = [this.report.name, this.reportElement.title, 'Detail']

      // Ensure we show all columns
      this.dataTableDialogTitle = title.join(' > ')

      this.showDataTableDialog = true
    },
    async previewChanges () {
      await this.report.runReport()
    },
    exportData (reportElement, includeGrandTotals = false) {
      let dataToExport = reportElement.rawData
      let headers = []

      // Export aggregated grand totals if requested
      if (includeGrandTotals && this.$refs.reportTable && this.$refs.reportTable.processedRows) {
        const processedRows = this.$refs.reportTable.processedRows
        const columns = this.$refs.reportTable.processedColumns || []
        headers = columns.map(column => column.label || column.name || column.field)
        dataToExport = processedRows.map(row => {
          return columns.map(column => {
            const cellValue = row[column.field]
            let value = cellValue && typeof cellValue === 'object' && 'value' in cellValue
              ? cellValue.value
              : cellValue

            // Apply local timezone conversion for datetime columns
            if (column.component === 'date-time' && value && !isNaN(Date.parse(value))) {
              const dateTime = new IsoDateTime(value).inTimeZone(this.timeZone)
              value = `${dateTime.toLocaleDateString()} ${dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            }

            return value
          })
        })
      } else {
        // Apply local timezone conversion for datetime columns
        const datetimeColumns = ['Created At', 'Placed At', 'Paid At', 'Voided At', 'Effective At', 'Collected At', 'Prepared At', 'Delivered At']
        const datetimeIndices = dataToExport[0]?.map((name, i) => datetimeColumns.includes(name) ? i : null).filter(i => i !== null) || []

        if (datetimeIndices.length > 0) {
          dataToExport = dataToExport.map(row =>
            Array.isArray(row)
              ? row.map((value, i) =>
                datetimeIndices.includes(i) && value && !isNaN(Date.parse(value))
                  ? `${new IsoDateTime(value).inTimeZone(this.timeZone).toLocaleDateString()} ${new IsoDateTime(value).inTimeZone(this.timeZone).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                  : value
              )
              : row
          )
        }
      }
      const allData = headers.length > 0 ? [headers, ...dataToExport] : dataToExport

      const content = _.map(allData, row => _.map(row, cell => wrapCsvValue(cell)).join(',')).join('\r\n')

      exportFile(
        `${this.report.fullTitle} - ${reportElement.title}.csv`,
        content,
        'text/csv'
      )
    },
    dragStart (event) {
      this.$emit('drag-start', this.reportElement.id)

      // Use set timeout because we want the drag bitmap to be the version prior to adding the dragging class
      setTimeout(() => { this.dragging = true }, 0)
    },
    dragEnd () {
      this.dragging = false
    },
    drop () {
      this.$emit('drag-end', this.reportElement.id, this.dropAfter)
      this.dragOver = 0
    },
    dragEnter (e) {
      if (this.dragging) return // Don't drop on self

      // Determine which edge we are closest to, drop before or after element
      const rect = this.$refs.reportElementWrapper.getBoundingClientRect()
      const xRatio = (e.clientX - rect.left) / (rect.right - rect.left) - 0.5
      const yRatio = (e.clientY - rect.top) / (rect.bottom - rect.top) - 0.5

      if (Math.abs(xRatio) > Math.abs(yRatio)) {
        this.dropAfter = xRatio > 0
        this.dropClass = this.dropAfter ? 'drop-right' : 'drop-left'
      } else {
        this.dropAfter = yRatio > 0
        this.dropClass = this.dropAfter ? 'drop-bottom' : 'drop-top'
      }

      this.dragOver += 1
    },
    dragLeave () {
      if (this.dragging) return

      this.dragOver -= 1
    },
    select () {
      this.$emit('select-element', this.reportElement)
    },
    unselect () {
      this.$emit('unselect-elements')
    }
  }
}
</script>

<style lang="scss" scoped>
.selected>div:first-child {
  border: solid 2px blue;
}

.drag-over.drop-left {
  border-left: solid 3px #000;
}
.drag-over.drop-right {
  border-right: solid 3px #000;
}

.drag-over.drop-top {
  border-top: solid 3px #000;
}
.drag-over.drop-bottom {
  border-bottom: solid 3px #000;
}

.dragging {
  border: solid 1px $grey-5;
}

.dragging > div {
  opacity: 0.3;
}

.migratable-banner {
  border: 2px solid palevioletred;
  text-align: left;
}

</style>

<style>

.report-element .download-link {
  visibility: hidden;
}

.report-element:hover .download-link {
  visibility: visible;
}

.report-element .help-icon {
  visibility: hidden;
}

.report-element:hover .help-icon {
  visibility: visible;
}

.report-element .data-freshness {
  visibility: hidden;
}

.report-element:hover .data-freshness {
  visibility: visible;
}

.report-element .full-screen-button {
  visibility: hidden;
}
.report-element:hover .full-screen-button {
  visibility: visible;
}
.report-element .search-button {
  visibility: hidden;
}
.report-element:hover .search-button {
  visibility: visible;
}

.report-element {
  vertical-align: top;
  text-align: center;
}

.report-col-12 {
  width: 100%;
  display: block;
}
.report-col-11 {
  width: 91.6667%;
  display: inline-block;
}
.report-col-10 {
  width: 83.3333%;
  display: inline-block;
}
.report-col-9 {
  width: 75%;
  display: inline-block;
}

.report-col-8  {
  width: 66.6667%;
  display: inline-block;
}

.report-col-7  {
  width: 58.3333%;
  display: inline-block;
}

.report-col-6  {
  width: 50%;
  display: inline-block;
}
.report-col-5  {
  width: 41.6667%;
  display: inline-block;
}

.report-col-4  {
  width: 33.3333%;
  display: inline-block;
}
.report-col-3  {
  width: 25%;
  display: inline-block;
}
.report-col-2  {
  width: 16.6667%;
  display: inline-block;
}
.report-col-1  {
  width: 8.3333%;
  display: inline-block;
}

@media (max-width: 700px) {
  .report-col-11 {
    width: 100%;
    display: block;
  }
  .report-col-10 {
    width: 100%;
    display: block;
  }
  .report-col-9 {
    width: 100%;
    display: block;
  }
    .report-col-8  {
      width: 100%;
      display: block;
    }
    .report-col-7  {
      width: 100%;
      display: block;
    }
    .report-col-6  {
      width: 100%;
      display: block;
    }
    .report-col-5  {
      width: 100%;
      display: block;
    }
    .report-col-4  {
      width: 50%;
      display: inline-block;
    }
    .report-col-3  {
      width: 50%;
      display: inline-block;
    }
    .report-col-2  {
      width: 50%;
      display: inline-block;
    }
    .report-col-1  {
      width: 50%;
      display: inline-block;
    }
  }

  @media (max-width: 500px) {
    .report-col-11 {
      width: 100%;
      display: block;
    }
    .report-col-10 {
      width: 100%;
      display: block;
    }
    .report-col-9 {
      width: 100%;
      display: block;
    }
    .report-col-8  {
      width: 100%;
      display: block;
    }
    .report-col-7  {
      width: 100%;
      display: block;
    }
    .report-col-6  {
      width: 100%;
      display: block;
    }
    .report-col-5  {
      width: 100%;
      display: block;
    }
    .report-col-4  {
      width: 100%;
      display: block;
    }
    .report-col-3  {
      width: 100%;
      display: block;
    }
    .report-col-2  {
      width: 100%;
      display: block;
    }
    .report-col-1  {
      width: 100%;
      display: block;
    }
  }

  @media print {
    .report-element {
      page-break-inside: avoid;
    }
  }
</style>
